import { useState, useEffect } from 'react'
import { useAuthStore, useThemeStore, usePlayerStore, useBlogStore } from './stores'
import { useBlogFixtureStore } from './stores/useBlogFixtureStore'
import AuthPage from './pages/AuthPage'
import Dashboard from './pages/Dashboard'
import PlayerDashboard from './components/PlayerDashboard'
import PlayerDetail from './components/PlayerDetail'
import AdminPanel from './pages/AdminPanel'
import ExplorePage from './pages/ExplorePage'
import LandingPage from './pages/LandingPage'
import AboutPage from './pages/AboutPage'
import HowItWorksPage from './pages/HowItWorksPage'
import TopTalentsPage from './pages/TopTalentsPage'
import SupportPage from './pages/SupportPage'
import PrivacyPolicyPage from './pages/PrivacyPolicyPage'
import ProfessionalTermsPage from './pages/ProfessionalTermsPage'
import PricingPage from './pages/PricingPage'
import BlogPage from './pages/BlogPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import PaystackCallback from './pages/PaystackCallback'
import { Navbar } from './components/Navbar'
import { Footer } from './components/Footer'
import { ScrollButtons } from './components/ScrollButtons'
import { ResetPasswordModal } from './components/ResetPasswordModal'
import { Loader2 } from 'lucide-react'

const VIEW_STORAGE_KEY = 'scoutang:view'
const VIEW_STORAGE_KEY_PERSISTENT = 'scoutang:view:persistent'
const ADMIN_PATH_SEGMENT = (import.meta.env.VITE_ADMIN_PATH || 'portal').replace(/^\/+|\/+$/g, '')
const ADMIN_PATHNAME = `/${ADMIN_PATH_SEGMENT}`
const RESET_PASSWORD_PATHNAME = '/reset-password'
const ROUTABLE_VIEWS = ['home', 'explore', 'top-talents', 'about', 'how-it-works', 'pricing', 'support', 'privacy', 'terms', 'blog', 'admin', 'dashboard', 'reset-password', 'player-dashboard', 'player-profile', 'paystack-callback'] as const
type RouteView = typeof ROUTABLE_VIEWS[number]

const isRouteView = (value: string | null): value is RouteView =>
  Boolean(value && ROUTABLE_VIEWS.includes(value as RouteView))

const extractPlayerIdFromHash = (): string | null => {
  const hash = window.location.hash.replace(/^#\/?/, '')
  if (hash.startsWith('player-profile/')) {
    return hash.split('/')[1] || null
  }
  return null
}

const readRouteView = () => {
  const url = new URL(window.location.href)
  if (url.pathname === '/blog' || url.pathname.startsWith('/blog/')) return 'blog'
  if (url.pathname === RESET_PASSWORD_PATHNAME) return 'reset-password'
  if (url.pathname === '/paystack-callback') return 'paystack-callback'

  const hashValue = window.location.hash.replace(/^#\/?/, '') || null

  if (hashValue && hashValue.startsWith('player-profile')) return 'player-profile'
  if (isRouteView(hashValue)) return hashValue

  const storedView = window.sessionStorage.getItem(VIEW_STORAGE_KEY) || window.localStorage.getItem(VIEW_STORAGE_KEY_PERSISTENT)
  return isRouteView(storedView) ? storedView : 'home'
}

const isAdminLocation = (url: URL) => url.pathname === ADMIN_PATHNAME

const writeRoute = (view: RouteView, replace = false, playerId?: string) => {
  const url = new URL(window.location.href)
  if (view === 'admin') {
    url.pathname = ADMIN_PATHNAME
    url.hash = ''
  } else if (view === 'reset-password') {
    url.pathname = RESET_PASSWORD_PATHNAME
    url.hash = ''
  } else if (view === 'home') {
    url.pathname = '/'
    url.hash = ''
  } else if (view === 'blog') {
    url.pathname = '/blog'
    url.hash = ''
  } else if (view === 'paystack-callback') {
    url.pathname = '/paystack-callback'
    url.hash = ''
  } else if (view === 'player-profile' && playerId) {
    url.pathname = '/'
    url.hash = `#/player-profile/${playerId}`
  } else {
    url.pathname = '/'
    url.hash = `#/${view}`
  }

  const method = replace ? 'replaceState' : 'pushState'
  window.history[method](null, '', url.toString())
}

export { writeRoute }

function App() {
  const { user, checkSession, isRecoveringPassword } = useAuthStore()
  const { isDark } = useThemeStore()
  const { fetchPlayers, fetchClubs, fetchCompetitions, fetchCompetitionFixtures, players } = usePlayerStore()
  const { fetchBlogPosts } = useBlogStore()
  const { fetchFixtures } = useBlogFixtureStore()
  const [showAuthPage, setShowAuthPage] = useState(false)
  const [initialPlayerId, setInitialPlayerId] = useState<string | null>(null)
  const [currentView, setCurrentView] = useState<RouteView>(readRouteView())
  const [publicPlayerId, setPublicPlayerId] = useState<string | null>(() => extractPlayerIdFromHash())
  const [isPwa, setIsPwa] = useState(false)
  const [initialLoadDone, setInitialLoadDone] = useState(false)

  const navigateToView = (view: RouteView, options?: { replace?: boolean }, playerId?: string) => {
    setCurrentView(view)
    window.sessionStorage.setItem(VIEW_STORAGE_KEY, view)
    window.localStorage.setItem(VIEW_STORAGE_KEY_PERSISTENT, view)
    writeRoute(view, options?.replace, playerId)
  }

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        await checkSession()
      } catch (error) {
        console.error('Failed to restore session before loading content:', error)
      }
      fetchPlayers()
      fetchClubs()
      fetchCompetitions()
      fetchCompetitionFixtures()
      fetchBlogPosts()
      fetchFixtures()
    }

    void loadInitialData()
    fetchFixtures()

    const isStandalone = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches
      || (window.navigator as any).standalone === true
    setIsPwa(Boolean(isStandalone))

    const url = new URL(window.location.href)
    const ref = url.searchParams.get('ref')
    if (ref) {
      sessionStorage.setItem('scouta:referral_code', ref)
    }

  }, [checkSession, fetchPlayers, fetchClubs, fetchCompetitions, fetchCompetitionFixtures, fetchBlogPosts, fetchFixtures])

  useEffect(() => {
    setInitialLoadDone(true)
  }, [])

  useEffect(() => {
    if (user && showAuthPage) {
      setShowAuthPage(false)
    }
  }, [user, showAuthPage])

  const handleLogout = async () => {
    await useAuthStore.getState().logout()
    setShowAuthPage(false)
    setInitialPlayerId(null)
    navigateToView('home', { replace: true })
  }

  useEffect(() => {
    const url = new URL(window.location.href)
    const isAdminUrl = isAdminLocation(url)

    const authCode = url.searchParams.get('code')
    const isRecoveryFromHash = url.hash.includes('type=recovery')

    if (isAdminUrl) {
      setCurrentView('admin')
      window.sessionStorage.setItem(VIEW_STORAGE_KEY, 'admin')
      window.localStorage.setItem(VIEW_STORAGE_KEY_PERSISTENT, 'admin')
    } else {
      const routeView = readRouteView()
      setCurrentView(routeView)
      window.sessionStorage.setItem(VIEW_STORAGE_KEY, routeView)
      window.localStorage.setItem(VIEW_STORAGE_KEY_PERSISTENT, routeView)
    }

    if (authCode && url.pathname === RESET_PASSWORD_PATHNAME) {
      setCurrentView('reset-password')
      window.sessionStorage.setItem(VIEW_STORAGE_KEY, 'reset-password')
      window.localStorage.setItem(VIEW_STORAGE_KEY_PERSISTENT, 'reset-password')
    }

    if (isRecoveryFromHash && url.pathname === RESET_PASSWORD_PATHNAME) {
      setCurrentView('reset-password')
      window.sessionStorage.setItem(VIEW_STORAGE_KEY, 'reset-password')
      window.localStorage.setItem(VIEW_STORAGE_KEY_PERSISTENT, 'reset-password')
    }

    const playerIdFromHash = extractPlayerIdFromHash()
    if (playerIdFromHash) {
      setCurrentView('player-profile')
      setPublicPlayerId(playerIdFromHash)
    } else {
      const playerId = url.searchParams.get('player')
      if (playerId) {
        if (url.searchParams.get('view') === 'profile') {
          setCurrentView('player-profile')
          setPublicPlayerId(playerId)
        } else {
          setInitialPlayerId(playerId)
          setCurrentView('explore')
        }
      }
    }
  }, [])

  const isResetPasswordRoute = currentView === 'reset-password'

  useEffect(() => {
    const handleLocationChange = () => {
      const url = new URL(window.location.href)
      const isAdminUrl = isAdminLocation(url)
      const nextView = isAdminUrl ? 'admin' : readRouteView()
      setCurrentView(nextView)
      window.sessionStorage.setItem(VIEW_STORAGE_KEY, nextView)
      window.localStorage.setItem(VIEW_STORAGE_KEY_PERSISTENT, nextView)

      const playerIdFromHash = extractPlayerIdFromHash()
      if (playerIdFromHash) {
        setPublicPlayerId(playerIdFromHash)
      }
    }

    window.addEventListener('hashchange', handleLocationChange)
    window.addEventListener('popstate', handleLocationChange)
    return () => {
      window.removeEventListener('hashchange', handleLocationChange)
      window.removeEventListener('popstate', handleLocationChange)
    }
  }, [])

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }, [isDark])

  // Admin View
  if (currentView === 'admin') {
    if (user?.role === 'admin' || user?.role === 'sub_admin') {
      return (
        <div className={`${isDark ? 'dark' : ''} min-h-screen`}>
          <div className="surface-bg min-h-screen">
            <Dashboard initialPlayerId={initialPlayerId} />
            <ScrollButtons />
          </div>
        </div>
      )
    }

    return (
      <div className={`${isDark ? 'dark' : ''} min-h-screen flex flex-col`}>
        <Navbar
          currentView={currentView}
          onNavigate={(view) => navigateToView(view as RouteView)}
          onLoginClick={() => setShowAuthPage(true)}
          onLogoutClick={handleLogout}
          user={user}
        />

        <div className="flex-1">
          <AdminPanel
            onClose={() => {
              navigateToView('home', { replace: true })
            }}
            onRequestLogin={() => {
              setShowAuthPage(true)
            }}
          />
        </div>

        <Footer onNavigate={(view) => navigateToView(view as RouteView)} />

        {showAuthPage && (
          <AuthPage
            onClose={() => setShowAuthPage(false)}
            onNavigate={(view) => {
              setShowAuthPage(false)
              navigateToView(view as RouteView)
            }}
          />
        )}

        {isRecoveringPassword && !isResetPasswordRoute && <ResetPasswordModal />}
        <ScrollButtons />
      </div>
    )
  }

  if (isResetPasswordRoute) {
    return (
      <ResetPasswordPage
        onBackToLogin={() => {
          navigateToView('home', { replace: true })
        }}
      />
    )
  }

  // Authenticated Dashboard
  if (user && (currentView === 'home' || currentView === 'dashboard')) {
    if (user.role === 'player') {
      return (
        <div className={`${isDark ? 'dark' : ''} min-h-screen`}>
          <div className="surface-bg min-h-screen">
            <PlayerDashboard />
            <ScrollButtons />
          </div>
        </div>
      )
    }
    return (
      <div className={`${isDark ? 'dark' : ''} min-h-screen`}>
        <div className="surface-bg min-h-screen">
          <Dashboard initialPlayerId={initialPlayerId} initialTab={user.role === 'club' ? 'club' : undefined} />
          <ScrollButtons />
        </div>
      </div>
    )
  }

  // If opened as a PWA and user is not authenticated, show the auth page as the first screen
  if (isPwa && !user) {
    return (
      <div className={`${isDark ? 'dark' : ''} min-h-screen flex items-center justify-center bg-surface-bg`}> 
        <AuthPage
          onClose={() => { /* intentionally no-op: require auth to proceed in PWA */ }}
          onNavigate={(view) => {
            setShowAuthPage(false)
            navigateToView(view as RouteView)
          }}
        />
      </div>
    )
  }

  // Public/Marketing Pages
  const renderView = () => {
    switch (currentView) {
      case 'explore':
        return (
          <ExplorePage
            onLoginClick={() => setShowAuthPage(true)}
            selectedPlayerId={initialPlayerId}
          />
        )
      case 'top-talents':
        return (
          <TopTalentsPage 
            onLoginClick={() => setShowAuthPage(true)}
          />
        )
      case 'about':
        return (
          <AboutPage 
            onLoginClick={() => setShowAuthPage(true)}
            onExplore={() => navigateToView('explore')}
          />
        )
      case 'how-it-works':
        return (
          <HowItWorksPage 
            onLoginClick={() => setShowAuthPage(true)} 
          />
        )
      case 'pricing':
        return (
          <PricingPage 
            onLoginClick={() => setShowAuthPage(true)} 
          />
        )
      case 'support':
        return <SupportPage onNavigate={(view) => navigateToView(view as RouteView)} />
      case 'privacy':
        return <PrivacyPolicyPage onBack={() => navigateToView('home')} />
      case 'terms':
        return <ProfessionalTermsPage onBack={() => navigateToView('home')} />
      case 'blog':
        return <BlogPage />
      case 'player-profile':
        if (!publicPlayerId) {
          return (
            <ExplorePage
              onLoginClick={() => setShowAuthPage(true)}
            />
          )
        }
        const profilePlayer = players.find(p => p.id === publicPlayerId)
        if (profilePlayer) {
          return (
            <PlayerDetail
              player={profilePlayer}
              onBack={() => {
                setPublicPlayerId(null)
                navigateToView('explore')
              }}
              canEdit={false}
            />
          )
        }
        if (players.length === 0 && !initialLoadDone) {
          return (
            <div className="min-h-screen surface-bg flex items-center justify-center">
              <div className="flex flex-col items-center gap-4">
                <Loader2 className="text-[#7cff01] animate-spin" size={32} />
                <p className="text-gray-400 text-sm font-bold uppercase tracking-widest">Loading profile...</p>
              </div>
            </div>
          )
        }
        return (
          <div className="min-h-screen surface-bg flex items-center justify-center">
            <div className="text-center">
              <p className="text-slate-200 mb-2">This profile is not available.</p>
              <p className="text-slate-300 text-sm mb-4">It may be pending approval or the link is invalid.</p>
              <button
                onClick={() => {
                  setPublicPlayerId(null)
                  navigateToView('explore')
                }}
                className="btn-primary"
              >
                Back to Explore
              </button>
            </div>
          </div>
        )
      case 'paystack-callback':
        return <PaystackCallback />
      case 'home':
      default:
        return (
          <LandingPage
            onLoginClick={() => setShowAuthPage(true)}
            onExplore={() => navigateToView('explore')}
            onNavigate={(view) => navigateToView(view as RouteView)}
          />
        )
    }
  }

  return (
    <div className={`${isDark ? 'dark' : ''} min-h-screen flex flex-col`}>
      <Navbar 
        currentView={currentView}
        onNavigate={(view) => navigateToView(view as RouteView)}
        onLoginClick={() => setShowAuthPage(true)}
        onLogoutClick={handleLogout}
        user={user}
      />
      
      <div className="flex-1">
        {renderView()}
      </div>

      <Footer onNavigate={(view) => navigateToView(view as RouteView)} />

      {showAuthPage && (
        <AuthPage
          onClose={() => setShowAuthPage(false)}
          onNavigate={(view) => {
            setShowAuthPage(false)
            navigateToView(view as RouteView)
          }}
        />
      )}
      {isRecoveringPassword && !isResetPasswordRoute && <ResetPasswordModal />}
      <ScrollButtons />
    </div>
  )
}

export default App
